<!--
  Settings → Notifications, tab "History": every mail the ERP sent (SMTP direct
  OVH, table mail_log, routes /api/maillog), newest first, with its content,
  and "send again" / "send to someone else".

  Page à part (Settings → Mail History) jusqu'au 2026-10-18. Refaite dans le
  style des autres onglets ; la colonne « Mail » donne le nom du mail
  automatique (catalogue de app/helpers/automail.js) au lieu de la clé brute,
  et un renvoi demande confirmation en nommant les destinataires : il part
  dans de vraies boîtes.
-->
<template>
  <vx-card>
    <hv-section
      title="History"
      sub="Every mail the ERP sent, newest first: automatic mails, quotes, invoices, reminders. Open a line to read it, send it again, or send it to someone else.">
      <template slot="actions">
        <div class="mh-filters">
          <vs-input v-model="search" icon-pack="feather" icon="icon-search" placeholder="Search recipient or subject"
                    class="mh-filter mr-2" @keyup.enter="reload" />
          <vs-select v-model="statusFilter" class="mh-filter mr-2" @input="reload">
            <vs-select-item value="" text="All" />
            <vs-select-item value="sent" text="Sent" />
            <vs-select-item value="failed" text="Failed" />
          </vs-select>
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw" :disabled="loading" @click="reload">Refresh</vs-button>
        </div>
      </template>

      <p v-if="!loading && !error" class="hv2-muted mb-2">
        {{ count }} mail(s){{ count ? ` · ${offset + 1}–${offset + rows.length} shown` : '' }}
      </p>

      <hv-table :rows="rows" :loading="loading" :error="error" row-key="id"
                empty-icon="MailIcon" empty-text="No mail found.">
        <template slot="thead">
          <vs-th>When</vs-th><vs-th>To</vs-th><vs-th>Subject</vs-th><vs-th>Mail</vs-th><vs-th>Status</vs-th><vs-th></vs-th>
        </template>
        <template slot="row" slot-scope="{ r }">
          <vs-td class="hv2-nowrap">{{ roWhen(r.created_at) }}</vs-td>
          <vs-td><div class="mh-ellipsis" :title="r.to_addr">{{ r.to_addr }}</div></vs-td>
          <vs-td>
            <div class="mh-ellipsis" :title="r.subject">{{ r.subject }}</div>
            <div v-if="r.attachments && r.attachments.length" class="hv2-muted">
              <feather-icon icon="PaperclipIcon" svgClasses="w-3 h-3" /> {{ r.attachments.length }} attachment(s)
            </div>
          </vs-td>
          <vs-td><span :title="r.source || ''">{{ sourceName(r.source) }}</span></vs-td>
          <vs-td>
            <hv-pill :tone="r.status === 'sent' ? 'ok' : 'danger'" :label="r.status === 'sent' ? 'Sent' : 'Failed'"
                     :icon="r.status === 'sent' ? 'CheckIcon' : 'XCircleIcon'" :title="r.error || ''" />
          </vs-td>
          <vs-td>
            <div class="mh-actions">
              <vs-button size="small" type="flat" icon-pack="feather" icon="icon-eye" title="Read this mail" @click="openDetail(r)" />
              <vs-button size="small" type="flat" icon-pack="feather" icon="icon-send" title="Send it again" :disabled="sending" @click="confirmResend(r)" />
            </div>
          </vs-td>
        </template>
      </hv-table>

      <div v-if="count > limit" class="mh-pager">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-chevron-left" :disabled="offset === 0 || loading" @click="prevPage">Newer</vs-button>
        <span class="hv2-muted mx-3">page {{ Math.floor(offset / limit) + 1 }} of {{ Math.ceil(count / limit) }}</span>
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-chevron-right" icon-after :disabled="offset + rows.length >= count || loading" @click="nextPage">Older</vs-button>
      </div>
    </hv-section>

    <!-- One mail in full. Always mounted: Vuesax moves the popup into document.body. -->
    <vs-popup :title="detail ? detail.subject || 'Mail' : 'Mail'" :active.sync="detailActive">
      <div v-if="!detail" class="hv2-loading">
        <vs-progress indeterminate color="primary" class="mb-2" />Reading…
      </div>
      <div v-else class="hv2">
        <div class="hv2-kv"><div class="hv2-kv-k">Status</div>
          <div class="hv2-kv-v">
            <hv-pill :tone="detail.status === 'sent' ? 'ok' : 'danger'" :label="detail.status === 'sent' ? 'Sent' : 'Failed'" />
            <span class="hv2-muted ml-2">{{ roWhen(detail.created_at) }}<template v-if="detail.created_by"> · by {{ detail.created_by }}</template></span>
            <div v-if="detail.status === 'failed' && detail.error" class="hv2-sync-error">{{ detail.error }}</div>
          </div></div>
        <div class="hv2-kv"><div class="hv2-kv-k">From</div><div class="hv2-kv-v mh-v">{{ detail.from_addr }}</div></div>
        <div class="hv2-kv"><div class="hv2-kv-k">To</div><div class="hv2-kv-v mh-v">{{ detail.to_addr }}</div></div>
        <div v-if="detail.cc_addr" class="hv2-kv"><div class="hv2-kv-k">Copy</div><div class="hv2-kv-v mh-v">{{ detail.cc_addr }}</div></div>
        <div v-if="detail.reply_to" class="hv2-kv"><div class="hv2-kv-k">Reply to</div><div class="hv2-kv-v mh-v">{{ detail.reply_to }}</div></div>
        <div v-if="detail.source" class="hv2-kv"><div class="hv2-kv-k">Mail</div><div class="hv2-kv-v mh-v">{{ sourceName(detail.source) }} <span class="hv2-muted hv2-code">{{ detail.source }}</span></div></div>
        <div v-if="detail.attachments && detail.attachments.length" class="hv2-kv"><div class="hv2-kv-k">Attachments</div>
          <div class="hv2-kv-v mh-v">
            <div v-for="(a, i) in detail.attachments" :key="i">
              <feather-icon icon="PaperclipIcon" svgClasses="w-3 h-3" /> {{ a.filename }}
              <span v-if="a.size" class="hv2-muted">({{ fmtSize(a.size) }})</span>
            </div>
          </div></div>
        <div v-if="detail.message_id" class="hv2-kv"><div class="hv2-kv-k">Message-ID</div><div class="hv2-kv-v mh-v hv2-code">{{ detail.message_id }}</div></div>

        <div class="hv2-subhead mt-4">Content</div>
        <iframe v-if="detail.body_html" class="mh-body" sandbox="" :srcdoc="detail.body_html"></iframe>
        <div v-else class="hv2-muted">No HTML content kept for this mail.</div>

        <div class="sumal-settings-actions mt-4">
          <vs-button size="small" icon-pack="feather" icon="icon-send" :disabled="sending" @click="confirmResend(detail)">Send again</vs-button>
          <vs-button size="small" type="border" class="ml-2" icon-pack="feather" icon="icon-user-plus" @click="openSendTo">Send to someone else…</vs-button>
        </div>

        <div v-if="sendToActive" class="mh-sendto">
          <div class="hv2-subhead">Send to other recipients</div>
          <vs-input v-model="sendTo.to" label="To (separated by ;)" class="w-full mb-2" placeholder="name@example.com; other@example.com" />
          <vs-input v-model="sendTo.cc" label="Copy (optional)" class="w-full mb-3" placeholder="cc@example.com" />
          <vs-button size="small" :disabled="sending || !sendTo.to.trim()" @click="doSendTo">{{ sending ? 'Sending…' : 'Send' }}</vs-button>
          <vs-button size="small" type="flat" class="ml-2" @click="sendToActive = false">Cancel</vs-button>
        </div>
      </div>
    </vs-popup>
  </vx-card>
</template>

<script>
import axios from '@/axios.js'
import HvSection from '@/views/harvesting2/components/HvSection'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvPill from '@/views/harvesting2/components/HvPill'
import { roWhen } from '@/helpers/romaniaTime'

export default {
  name: 'MailHistory',
  components: { HvSection, HvTable, HvPill },
  data () {
    return {
      rows: [],
      count: 0,
      limit: 25,
      offset: 0,
      search: '',
      statusFilter: '',
      loading: false,
      error: '',
      sending: false,
      detail: null,
      detailActive: false,
      sendToActive: false,
      sendTo: { to: '', cc: '' },
      names: {}
    }
  },
  methods: {
    roWhen,
    errMsg (e) {
      return (e && e.response && e.response.data && (e.response.data.message || e.response.data)) || (e && e.message) || String(e)
    },
    notify (color, title, text) { this.$vs.notify({ color, title, text, position: 'bottom-right', time: color === 'danger' ? 8000 : 5000 }) },
    // The name of an automatic mail from its source key; any other source as it is.
    sourceName (src) { return src ? this.names[src] || src : '—' },
    loadNames () {
      axios.get('/automatic-mails')
        .then((r) => {
          const names = {}
          for (const m of r.data || []) {
            names[m.key] = m.name
            if (m.source) names[m.source] = m.name
          }
          this.names = names
        })
        .catch(() => {})
    },
    async fetch () {
      this.loading = true
      this.error = ''
      try {
        const params = { limit: this.limit, offset: this.offset }
        if (this.search.trim()) params.q = this.search.trim()
        if (this.statusFilter) params.status = this.statusFilter
        const res = await axios.get('/maillog', { params })
        this.rows = (res.data && res.data.rows) || []
        this.count = (res.data && res.data.count) || 0
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    reload () { this.offset = 0; this.fetch() },
    nextPage () { if (this.offset + this.rows.length < this.count) { this.offset += this.limit; this.fetch() } },
    prevPage () { this.offset = Math.max(0, this.offset - this.limit); this.fetch() },
    async openDetail (r) {
      this.detail = null
      this.detailActive = true
      this.sendToActive = false
      try {
        const res = await axios.get(`/maillog/${r.id}`)
        this.detail = res.data
      } catch (e) {
        this.detailActive = false
        this.notify('danger', 'Cannot read this mail', this.errMsg(e))
      }
    },
    openSendTo () {
      this.sendTo = { to: '', cc: '' }
      this.sendToActive = true
    },
    // A send reaches real mailboxes: name the recipients before.
    confirmResend (r) {
      if (!r) return
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Send this mail again?',
        text: `To: ${r.to_addr || '(no recipient)'}${r.cc_addr ? ` · copy: ${r.cc_addr}` : ''}`,
        acceptText: 'Send',
        cancelText: 'Cancel',
        accept: () => this.resend(r)
      })
    },
    async resend (r) {
      this.sending = true
      try {
        const res = await axios.post(`/maillog/${r.id}/resend`, {})
        if (res.data && res.data.ok) {
          this.notify('success', 'Sent again', r.to_addr || '')
          this.reload()
        } else {
          this.notify('danger', 'Not sent', (res.data && res.data.message) || 'Unknown error')
        }
      } catch (e) {
        this.notify('danger', 'Not sent', this.errMsg(e))
      } finally {
        this.sending = false
      }
    },
    async doSendTo () {
      if (!this.detail || !this.sendTo.to.trim()) return
      this.sending = true
      try {
        const res = await axios.post(`/maillog/${this.detail.id}/resend`, { to: this.sendTo.to, cc: this.sendTo.cc })
        if (res.data && res.data.ok) {
          this.notify('success', 'Sent', `To ${this.sendTo.to}`)
          this.sendToActive = false
          this.reload()
        } else {
          this.notify('danger', 'Not sent', (res.data && res.data.message) || 'Unknown error')
        }
      } catch (e) {
        this.notify('danger', 'Not sent', this.errMsg(e))
      } finally {
        this.sending = false
      }
    },
    fmtSize (bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    }
  },
  created () {
    this.loadNames()
    this.fetch()
  }
}
</script>
